import i18n from "@sg/i18n";
import { invalidateCache } from "@sg/query";
import { uniq } from "lodash";
import { useMemo } from "react";
import { useMutation, useQuery } from "react-query";
import { useParams } from "react-router-dom";
import { z } from "zod";
import { AuditableSchema, ContractType, DayCountConvention, Side } from "~/types";
import { agent } from "~/util/agent";
import { useDownloadDoc, useUploadDoc } from "./document";

enum NoticePeriodDayType {
  CALENDAR = "Calendar",
  BUSINESS = "Business",
}

enum NotionalType {
  FIXED_SHARES = "FixedShares",
  FIXED_USD_NOTIONAL = "FixedUSDNotional",
}

/** Values accepted by equity swap contract `allowed_instrument_configs[].instrument_type` (API). */
enum EquitySwapContractInstrumentType {
  currency_pair = "currency_pair",
  option = "option",
  forward = "forward",
}

const ContractSpotSchema = z.object({
  name: z.string().optional(),
  contract_counterparties: z.array(
    z.object({
      counterparty_id: z
        .string()
        .uuid(i18n.t("validation:resource_required", { resource: i18n.t("resource:counterparty") }))
        .min(1),
      is_payer: z.boolean(),
    })
  ),
  comment: z.string().optional(),
  documents: z.array(z.instanceof(File)),
});

const BaseContractSpotSchemaSchema = ContractSpotSchema.merge(AuditableSchema);

const ContractEquitySwapSchema = z.object({
  name: z.string().optional(),
  is_master_agreement: z.boolean().optional(),
  isda_agreement_date: z.string().optional(),
  markup_pct: z.string().optional(),
  exit_trading_fee_pct: z.string().optional(),
  price_improvement_pct: z.string().optional(),
  funding_spread_pct: z.string().optional(),
  rebalancing_threshold_pct: z.string().optional(),
  contract_counterparties: z.array(
    z.object({
      counterparty_id: z
        .string()
        .uuid(i18n.t("validation:resource_required", { resource: i18n.t("resource:counterparty") }))
        .min(1),
      threshold: z.string(),
      min_transfer_amount: z.string(),
      transfer_rounding_amount: z.string().optional(),
      lei: z.string().optional(),
      legal_name: z.string().optional(),
      notice_period_days: z.union([z.string(), z.number()]).optional(),
      notice_period_day_type: z.string().optional(),
      is_hedging_party: z.boolean().optional(),
      side: z.nativeEnum(Side).optional(),
      independent_amount_pct: z.string().optional(),
      min_collateral_buffer_pct: z.string().optional(),
    })
  ),
  allowed_instrument_configs: z
    .array(
      z.object({
        instrument_type: z.string().min(1),
        notional_type: z.nativeEnum(NotionalType).optional(),
      })
    )
    .optional(),
  confirm_templates: z
    .array(
      z.object({
        instrument_type: z.string().min(1),
        notional_type: z.nativeEnum(NotionalType).optional(),
        s3_key: z.any().optional(),
        is_custom: z.boolean().optional(),
      })
    )
    .optional(),
  documents: z.array(z.instanceof(File)),
});

const BaseContractEquitySwapSchema = ContractEquitySwapSchema.merge(AuditableSchema);

const ContractLoanSchema = z.object({
  name: z.string().optional(),
  is_master_agreement: z.boolean().optional(),
  contract_counterparties: z.array(
    z.object({
      counterparty_id: z
        .string()
        .uuid(i18n.t("validation:resource_required", { resource: i18n.t("resource:counterparty") }))
        .min(1),
    })
  ),
  day_count: z.nativeEnum(DayCountConvention),
  documents: z.array(z.instanceof(File)),
});

const BaseContractLoanSchema = ContractLoanSchema.merge(AuditableSchema);

type ContractSpotResourceType = z.infer<typeof BaseContractSpotSchemaSchema>;
type CreateContractSpotInput = z.infer<typeof ContractSpotSchema>;

type ContractEquitySwapResourceType = z.infer<typeof BaseContractEquitySwapSchema>;
type CreateContractEquitySwapInput = z.infer<typeof ContractEquitySwapSchema>;

type EquitySwapContractCounterpartyInput = CreateContractEquitySwapInput["contract_counterparties"][number];

function normalizeEquitySwapContractCounterpartyForApi(cp: EquitySwapContractCounterpartyInput, side: Side) {
  const raw = cp.notice_period_days;
  let notice_period_days: number | undefined;
  if (raw !== undefined && raw !== null && raw !== "") {
    const n = typeof raw === "number" ? raw : parseInt(String(raw), 10);
    notice_period_days = Number.isNaN(n) ? undefined : n;
  }

  return {
    counterparty_id: cp.counterparty_id,
    threshold: cp.threshold,
    min_transfer_amount: cp.min_transfer_amount,
    side,
    transfer_rounding_amount: cp.transfer_rounding_amount ?? "0.0",
    is_hedging_party: cp.is_hedging_party ?? false,
    ...(cp.lei ? { lei: cp.lei } : {}),
    ...(cp.legal_name ? { legal_name: cp.legal_name } : {}),
    ...(notice_period_days !== undefined ? { notice_period_days } : {}),
    ...(cp.notice_period_day_type ? { notice_period_day_type: cp.notice_period_day_type } : {}),
    ...(cp.independent_amount_pct ? { independent_amount_pct: cp.independent_amount_pct } : {}),
    ...(cp.min_collateral_buffer_pct ? { min_collateral_buffer_pct: cp.min_collateral_buffer_pct } : {}),
  };
}

type ContractLoanResourceType = z.infer<typeof BaseContractLoanSchema>;
type CreateContractLoanInput = z.infer<typeof ContractLoanSchema>;

const QUERY_CONTRACTS_KEY = "contracts";
const QUERY_CONTRACT_KEY = "contract";

type ContractResourceType = ContractEquitySwapResourceType | ContractLoanResourceType;
type CreateContractInput = CreateContractEquitySwapInput | CreateContractLoanInput;

const useContracts = (type: ContractType, cpId?: string) =>
  useQuery(
    [QUERY_CONTRACTS_KEY, type, cpId],
    async () => agent(`contracts/${type}/?counterparty_id=${cpId}`).json<ContractResourceType[]>(),
    {
      enabled: !!cpId,
    }
  );

const useCounterpartiesWithContract = (type: ContractType, cpId?: string) => {
  const query = useContracts(type, cpId);
  const { counterparties, groups } = useMemo(() => {
    return {
      counterparties: uniq(query.data?.flatMap((d) => d.contract_counterparties.map((c) => c.counterparty_id))),
      groups: query.data?.map((d) => d.contract_counterparties.map((c) => c.counterparty_id)) || [],
    };
  }, [query.data]);
  return { ...query, counterparties, groups };
};

const useContract = (type: ContractType, id?: string) =>
  useQuery([QUERY_CONTRACT_KEY, type, id], async () => agent(`contracts/${type}/${id}`).json<ContractResourceType>(), {
    enabled: !!id,
  });

const useCreateContract = (type: ContractType) => {
  const { cpId } = useParams();
  return useMutation<string, Error, FormData>(
    (data) => {
      return agent
        .post(`contracts/${type}/`, {
          body: data,
        })
        .json();
    },
    {
      onSuccess() {
        invalidateCache([QUERY_CONTRACTS_KEY, type, cpId], null);
      },
    }
  );
};

const useContractDoc = (contractId?: string) => {
  return useDownloadDoc(`contracts/${contractId}`);
};

const useUploadContractDoc = (contractId?: string) => {
  return useUploadDoc(`contracts/${contractId}`);
};

const useConfirmTemplates = (contractId?: string) => {
  return useQuery(
    ["confirm-templates", contractId],
    async () =>
      agent(`confirm-templates/?contract_id=${contractId}`).json<
        {
          id: string;
          instrument_type: string;
          notional_type?: NotionalType;
          s3_key?: string;
          is_custom?: boolean;
        }[]
      >(),
    {
      enabled: !!contractId,
    }
  );
};

const useCreateConfirmTemplate = () =>
  useMutation<string, Error, FormData>((data) => {
    return agent
      .post("confirm-templates/", {
        body: data,
      })
      .json();
  });

const useDeleteConfirmTemplate = () =>
  useMutation<string, Error, string>((id) => {
    return agent
      .delete(`confirm-templates/${id}`, {})
      .json();
  });

export {
  ContractEquitySwapSchema,
  ContractLoanSchema,
  EquitySwapContractInstrumentType,
  normalizeEquitySwapContractCounterpartyForApi,
  NotionalType,
  NoticePeriodDayType,
  ContractSpotSchema,
  useContract,
  useConfirmTemplates,
  useCreateConfirmTemplate,
  useDeleteConfirmTemplate,
  useContractDoc,
  useContracts,
  useCounterpartiesWithContract,
  useCreateContract,
  useUploadContractDoc,
};
export type {
  ContractResourceType,
  ContractSpotResourceType,
  CreateContractEquitySwapInput,
  CreateContractInput,
  CreateContractLoanInput,
  CreateContractSpotInput,
};
